import React, { useEffect, useMemo, useState } from 'react';

import PageHeader from 'src/core/ui/PageHeader';
import Footer from 'src/core/ui/helpers/Footer';
import InfoIcon from '@mui/icons-material/Info';

import CustomJoyride from 'src/shared/support/CustomJoyride';
import CustomButton from 'src/core/ui/CustomButton';
import useTour from 'src/shared/support/useTour';
import { useSettingsContext } from 'src/core/settings/useSettings';
import {
  useGetDashboardShipped,
  useGetDashboardPurchasing,
  useGetDashboardReturns,
  useGetDashboardSales,
  useGetDashboardToShip,
  useGetDashboardCreditNotes,
  useGetDashboardSalesMargins,
} from 'src/pages/dashboard/dashboardRequests';

import {
  buildDashboardSalesProvider,
  buildDashboardReturnsProvider,
  buildDashboardDispatchedProvider,
  buildDashboardToShipProvider,
  buildDashboardPurchasingProvider,
  buildDashboardCreditNotesProvider,
  composeProviders,
} from './DashboardProviders';
import { DashboardChartCard } from './DashboardGraph';
import {
  DashboardExecutiveSummaryCard,
  DashboardChannelBreakdownCard,
  DashboardOperationsOverviewCard,
  useDashboardCardMetrics,
} from './DashboardCards';
import { buildDashboardKpis, DashboardKpiOverviewCard } from './DashboardKpiOverview';
import { DashboardGrid } from './DashboardLayout';
import { DashboardPeriodFilter, DashboardSalesChannelFilter } from './DashboardFilters';
import { buildRangeTitle, useIsMobile } from './DashboardHelpers';
import ThemeSelector from 'src/core/settings/ThemeSettingButton';
import { useTranslation } from 'react-i18next';
import internationalization from 'src/shared/i18n/i18n';
import LanguageIdSetting from 'src/core/settings/LanguageSetting';
import type { DashboardBarKey } from 'src/pages/dashboard/DashboardGraph';
import type { Period, SalesChannel } from 'src/pages/dashboard/DashboardFilters';

const DEFAULT_DISPLAY_LANGUAGE = 1;
const DEFAULT_SELECTED_BARS: DashboardBarKey[] = ['ordersBar', 'ordersValueBar'];
// Stable empty array: `?? []` inline would create a new array every render and rebuild the providers/chart each time.
const EMPTY: any[] = [];

const mapLanguageIdToI18nCode = (id?: number) => {
  switch (Number(id)) {
    case 2:
      return 'se';
    case 7:
      return 'de';
    case 1:
    default:
      return 'en';
  }
};

const DashboardMain: React.FC = () => {
  const isMobile = useIsMobile(768);
  const { settings } = useSettingsContext();
  const { t } = useTranslation();

  const { toDate, dashboardFromDate } = useMemo(() => {
    const date = new Date();
    const to = date.toISOString().split('T')[0];
    date.setFullYear(date.getFullYear() - 1);
    return { toDate: to, dashboardFromDate: date.toISOString().split('T')[0] };
  }, []);

  const displayLanguageId = Number((settings as any)?.DisplayLanguage ?? DEFAULT_DISPLAY_LANGUAGE);

  useEffect(() => {
    const languageCode = mapLanguageIdToI18nCode(displayLanguageId);
    if (internationalization.language !== languageCode) {
      internationalization.changeLanguage(languageCode);
    }
  }, [displayLanguageId]);

  const [chartPeriod, setChartPeriod] = useState<Period>('Week');
  // What the chart shows (chips/presets on the chart card replace the old "Information" header filter)
  const [selectedBars, setSelectedBars] = useState<DashboardBarKey[]>(DEFAULT_SELECTED_BARS);
  const [selectedSalesChannels, setSelectedSalesChannels] = useState<SalesChannel[]>(['Online', 'Store']);

  const kpiSettings = (settings as any)?.KPI ?? (settings as any)?.Kpi ?? (settings as any) ?? {};

  const sales = useGetDashboardSales(dashboardFromDate, toDate) ?? EMPTY;
  const returns = useGetDashboardReturns(dashboardFromDate) ?? EMPTY;
  const shipped = useGetDashboardShipped(dashboardFromDate, toDate) ?? EMPTY;
  const toShip = useGetDashboardToShip(dashboardFromDate) ?? EMPTY;
  const purchasing = useGetDashboardPurchasing(dashboardFromDate) ?? EMPTY;
  const creditNotes = useGetDashboardCreditNotes(dashboardFromDate) ?? EMPTY;
  const salesMargins = useGetDashboardSalesMargins(dashboardFromDate, toDate) ?? EMPTY;

  const dataProvider = useMemo(
    () =>
      composeProviders(
        buildDashboardSalesProvider(sales as any),
        buildDashboardReturnsProvider(returns as any),
        buildDashboardDispatchedProvider(shipped as any),
        buildDashboardToShipProvider(toShip as any),
        buildDashboardPurchasingProvider(purchasing as any),
        buildDashboardCreditNotesProvider(creditNotes as any),
      ),
    [creditNotes, purchasing, returns, sales, shipped, toShip]
  );

  const tourProps = useTour ? useTour(['Dashboard', 'Purchasing Intro']) : { restartTour: () => console.log('Restart Tour Placeholder') };
  const rangeTitle = useMemo(() => buildRangeTitle(chartPeriod), [chartPeriod]);

  const kpis = useMemo(
    () => buildDashboardKpis({
      period: chartPeriod,
      dashboardSales: sales as any,
      dashboardReturns: returns as any,
      dashboardPurchasing: purchasing as any,
      settingsKpi: kpiSettings,
      t,
    }),
    [chartPeriod, sales, returns, purchasing, kpiSettings, t]
  );

  const cardMetrics = useDashboardCardMetrics({
    dataProvider,
    period: chartPeriod,
    dashboardSales: sales as any,
    dashboardReturns: returns as any,
    dashboardPurchasing: purchasing as any,
    dashboardDispatched: shipped as any,
    dashboardToShip: toShip as any,
    dashboardCreditNotes: creditNotes as any,
    dashboardSalesMargins: salesMargins as any,
  });

  return (
    <>
      {CustomJoyride && <CustomJoyride tourProps={tourProps} />}

      <PageHeader
        {...{
          title: t('Dashboard'),
          subTitle: t('Charts, stats and KPIs'),
          components: [
            { component: <ThemeSelector /> },
            { component: <LanguageIdSetting settingKey="DisplayLanguage" allowedLanguageIds={[1, 2]} tooltip={t('Display Language')} /> },
            { component: <DashboardSalesChannelFilter value={selectedSalesChannels} onChange={setSelectedSalesChannels} />, isInGroupedMenu: isMobile },
            { component: <DashboardPeriodFilter value={chartPeriod} onChange={setChartPeriod} />, isInGroupedMenu: isMobile },
            { component: <CustomButton iconButton title={t('Help')} icon={<InfoIcon />} onClick={() => tourProps.restartTour()} /> },
          ],
        }}
      />

      {/* One layout for all widths: DashboardGrid reflows on the dashboard's own width (container queries). */}
      <DashboardGrid>
        <DashboardChartCard
          period={chartPeriod}
          selectedBars={selectedBars}
          onSelectedBarsChange={setSelectedBars}
          selectedSalesChannels={selectedSalesChannels}
          dataProvider={dataProvider}
          rangeTitle={rangeTitle}
        />
        <DashboardExecutiveSummaryCard metrics={cardMetrics} period={chartPeriod} />
        <DashboardChannelBreakdownCard metrics={cardMetrics} period={chartPeriod} />
        <DashboardOperationsOverviewCard metrics={cardMetrics} period={chartPeriod} />
        <DashboardKpiOverviewCard kpis={kpis} />
      </DashboardGrid>

      <Footer />
    </>
  );
};

export default DashboardMain;
